import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS } from "../../constants/api-urls";
import "./CollectionDeliveryConfirmation.scss";

export default function CollectionDeliveryConfirmation() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auction, setAuction] = useState(null);
  const [deliveryConfirmation, setDeliveryConfirmation] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [payment, setPayment] = useState(null);
  const [user, setUser] = useState(null);
  const [isSeller, setIsSeller] = useState(false);
  const [isBuyer, setIsBuyer] = useState(false);
  const [sellerNotes, setSellerNotes] = useState("");
  const [buyerNotes, setBuyerNotes] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [syncingPayment, setSyncingPayment] = useState(false);

  useEffect(() => {
    const currentUser = StorageService.getUser();
    setUser(currentUser);
    
    if (currentUser) {
      const userTypeIds = currentUser.user_type_id
        ? currentUser.user_type_id.toString().split(',').map((id) => parseInt(id.trim(), 10))
        : [];
      const isSellerRole =
        currentUser.company_role === 'seller' ||
        currentUser.company_role === 'BOTH' ||
        userTypeIds.includes(2) ||
        userTypeIds.includes(3);
      const isBuyerRole =
        currentUser.company_role === 'buyer' ||
        currentUser.company_role === 'BOTH' ||
        userTypeIds.includes(1) ||
        userTypeIds.includes(3);
      setIsSeller(isSellerRole);
      setIsBuyer(isBuyerRole);
    }
    
    fetchData();
  }, [auctionId]);

  useEffect(() => {
    if (deliveryConfirmation?.sla_deadline) {
      const interval = setInterval(() => {
        calculateTimeRemaining();
      }, 1000);
      calculateTimeRemaining();
      return () => clearInterval(interval);
    }
  }, [deliveryConfirmation]);

  const handleSyncPayment = async () => {
    try {
      setSyncingPayment(true);
      const response = await CommonService.post(
        API_URLS.AUCTION.COMPLETE_AUCTION_PAYMENT(auctionId),
        { payment_method: payment?.payment_method || "Escrow Service" },
      );
      if (!response?.success) {
        throw new Error(response?.message || "Could not record payment");
      }
      toast.success(
        response.data?.already_completed
          ? "Payment confirmed — opening collection & delivery"
          : "Payment recorded — collection & delivery is now unlocked",
      );
      await fetchData();
    } catch (error) {
      console.error("Error syncing payment:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not confirm payment. Try completing payment again.",
      );
    } finally {
      setSyncingPayment(false);
    }
  };

  const calculateTimeRemaining = () => {
    if (!deliveryConfirmation?.sla_deadline) return;
    
    const now = new Date();
    const deadline = new Date(deliveryConfirmation.sla_deadline);
    const diff = deadline - now;
    
    if (diff <= 0) {
      setTimeRemaining({ hours: 0, minutes: 0, expired: true });
      return;
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    setTimeRemaining({ hours, minutes, expired: false });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch auction
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
        
        // Fetch seller profile
        if (auctionResponse.data.user_login_id) {
          try {
            const sellerProfileResponse = await CommonService.get(`/company/profile/${auctionResponse.data.user_login_id}`);
            if (sellerProfileResponse && sellerProfileResponse.success) {
              setSellerProfile(sellerProfileResponse.data);
            }
          } catch (err) {
            console.error("Error fetching seller profile:", err);
          }
        }
      }
      
      // Fetch delivery confirmation (404 is normal before payment completes)
      let confirmationLoaded = false;
      try {
        const confirmationResponse = await CommonService.get(API_URLS.AUCTION.GET_DELIVERY_CONFIRMATION(auctionId));
        if (confirmationResponse && confirmationResponse.success) {
          const confirmation = confirmationResponse.data;
          confirmationLoaded = true;
          setDeliveryConfirmation(confirmation);
          setSellerNotes(confirmation.seller_notes || "");
          setBuyerNotes(confirmation.buyer_notes || "");
          
          let paymentData = null;
          if (confirmation.payment_id) {
            try {
              const paymentResponse = await CommonService.get(API_URLS.AUCTION.GET_PAYMENT(confirmation.payment_id));
              if (paymentResponse && paymentResponse.success) {
                paymentData = paymentResponse.data;
                setPayment(paymentData);
              }
            } catch (err) {
              console.error("Error fetching payment:", err);
            }
          }
          
          const buyerUserId = confirmation.buyer_user_id || paymentData?.buyer_user_id;
          if (buyerUserId) {
            try {
              const buyerProfileResponse = await CommonService.get(`/company/profile/${buyerUserId}`);
              if (buyerProfileResponse && buyerProfileResponse.success) {
                setBuyerProfile(buyerProfileResponse.data);
              }
            } catch (err) {
              console.error("Error fetching buyer profile:", err);
            }
          }
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status !== 404) {
          console.error("Error fetching delivery confirmation:", err);
          toast.error("Failed to load delivery confirmation");
        }
      }

      if (!confirmationLoaded) {
        try {
          const paymentResponse = await CommonService.get(API_URLS.AUCTION.GET_PAYMENT_BY_AUCTION(auctionId));
          if (paymentResponse?.success && paymentResponse.data) {
            setPayment(paymentResponse.data);
          }
        } catch (err) {
          if (err?.response?.status !== 404) {
            console.error("Error fetching payment by auction:", err);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSellerConfirmation = async () => {
    if (!deliveryConfirmation?.confirmation_id) {
      toast.error("Confirmation not found");
      return;
    }

    try {
      const response = await CommonService.put(
        API_URLS.AUCTION.UPDATE_SELLER_CONFIRMATION(deliveryConfirmation.confirmation_id),
        {
          confirmation: "confirmed",
          notes: sellerNotes.trim() || undefined
        }
      );

      if (response && response.success) {
        toast.success("Product handover confirmed successfully!");
        fetchData(); // Refresh data
      } else {
        toast.error(response?.message || "Failed to confirm handover");
      }
    } catch (error) {
      console.error("Error confirming handover:", error);
      toast.error("Failed to confirm handover");
    }
  };

  const handleBuyerConfirmation = async () => {
    if (!deliveryConfirmation?.confirmation_id) {
      toast.error("Confirmation not found");
      return;
    }

    try {
      const response = await CommonService.put(
        API_URLS.AUCTION.UPDATE_BUYER_CONFIRMATION(deliveryConfirmation.confirmation_id),
        {
          confirmation: "confirmed",
          notes: buyerNotes.trim() || undefined
        }
      );

      if (response && response.success) {
        toast.success("Product receipt confirmed successfully!");
        fetchData(); // Refresh data
      } else {
        toast.error(response?.message || "Failed to confirm receipt");
      }
    } catch (error) {
      console.error("Error confirming receipt:", error);
      toast.error("Failed to confirm receipt");
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    // Format time from HH:MM:SS to HH:MM AM/PM
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "pm" : "am";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="collection-delivery-page">
        <div className="loading-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="collection-delivery-page">
        <div className="error-container">
          <p>Auction not found.</p>
          <button onClick={() => navigate("/auctions")} className="btn-back">
            Back to Auctions
          </button>
        </div>
      </div>
    );
  }

  if (!deliveryConfirmation) {
    const sellerOwnsAuction = user && Number(auction.user_login_id) === Number(user.user_login_id);
    const noWinningBid = !auction.winning_bid_id;
    const sellerDecisionPending = String(auction.seller_decision || '').toLowerCase() !== 'accepted';
    const paymentStatus = String(payment?.payment_status || '').toLowerCase();
    const paymentCompleted = paymentStatus === 'completed';
    const paymentInProgress =
      payment &&
      !paymentCompleted &&
      ['pending', 'initiated', 'processing', 'in_progress', 'part_paid'].includes(paymentStatus || 'pending');

    let heading = "Collection & delivery isn't ready yet";
    let message = "This auction is not yet at the collection & delivery stage.";
    let primaryAction = null;

    if (noWinningBid) {
      message = "This auction hasn't ended with a winning bid yet, so there's nothing to collect or deliver.";
      primaryAction = { label: "View Auction", to: `/auction/${auctionId}` };
    } else if (sellerDecisionPending) {
      if (sellerOwnsAuction) {
        message = "You haven't accepted the winning bid yet. Once you accept, the buyer can complete payment and the collection & delivery flow will open here.";
        primaryAction = { label: "Review Winning Bid", to: `/auction/${auctionId}/winning-bid-review` };
      } else {
        message = "The seller hasn't accepted the winning bid yet. You'll be able to confirm collection here once the seller accepts and you complete payment.";
        primaryAction = { label: "Back to My Bids", to: "/auction/my/bids" };
      }
    } else if (paymentCompleted) {
      heading = "Collection & delivery is being prepared";
      message = "Payment is complete. Refresh this page in a moment — if confirmation still doesn't appear, contact support.";
      primaryAction = { label: "Refresh", to: null, refresh: true };
    } else if (paymentInProgress) {
      heading = "Payment in progress";
      if (sellerOwnsAuction) {
        message = "The buyer has started payment but hasn't finished yet. Collection & delivery will unlock here once payment is completed.";
        primaryAction = { label: "Back to My Auctions", to: "/auction/my/auctions" };
      } else {
        message = "Your payment is in progress. Complete payment to unlock the collection & delivery confirmation.";
        primaryAction = {
          label: "Continue Payment",
          to: `/auction/${auctionId}/payment/process`,
        };
      }
    } else {
      heading = "Winner declared — payment required";
      if (sellerOwnsAuction) {
        message = "The winning bid has been accepted. Collection & delivery will appear here once the buyer completes payment.";
        primaryAction = { label: "Back to My Auctions", to: "/auction/my/auctions" };
      } else {
        message =
          "The seller has accepted your winning bid. Complete payment to unlock the collection & delivery confirmation.";
        primaryAction = { label: "Complete Payment", to: `/auction/${auctionId}/payment` };
      }
    }

    const showSyncPayment =
      !sellerOwnsAuction &&
      !noWinningBid &&
      !sellerDecisionPending &&
      !paymentCompleted &&
      !paymentInProgress;

    return (
      <div className="collection-delivery-page">
        <div className="error-container">
          <h3 style={{ marginBottom: 8 }}>{heading}</h3>
          <p>{message}</p>
          {showSyncPayment && (
            <p style={{ marginTop: 8, fontSize: "0.9rem", opacity: 0.85 }}>
              Already paid on the gateway? Use the button below to record it and unlock this page.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            {showSyncPayment && (
              <button
                onClick={() => void handleSyncPayment()}
                className="btn-back"
                disabled={syncingPayment}
              >
                {syncingPayment ? "Confirming…" : "I've already paid — unlock collection"}
              </button>
            )}
            {primaryAction && (
              <button
                onClick={() => {
                  if (primaryAction.refresh) {
                    void fetchData();
                  } else {
                    navigate(primaryAction.to);
                  }
                }}
                className="btn-back"
              >
                {primaryAction.label}
              </button>
            )}
            <button onClick={() => navigate('/auction/my/bids')} className="btn-back">
              My Bids
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sellerConfirmed = deliveryConfirmation.seller_confirmation === "confirmed";
  const buyerConfirmed = deliveryConfirmation.buyer_confirmation === "confirmed";
  const transactionComplete = deliveryConfirmation.transaction_status === "completed";

  const viewerUserId = user ? Number(user.user_login_id ?? user.id) : null;
  const isAuctionOwner =
    viewerUserId != null && Number(auction.user_login_id) === viewerUserId;
  const isConfirmationSeller =
    viewerUserId != null && Number(deliveryConfirmation.seller_user_id) === viewerUserId;
  const isConfirmationBuyer =
    viewerUserId != null && Number(deliveryConfirmation.buyer_user_id) === viewerUserId;
  const canActAsSeller = isSeller || isAuctionOwner || isConfirmationSeller;
  const canActAsBuyer = isBuyer || isConfirmationBuyer;

  return (
    <div className="collection-delivery-page">
      <div className="container">
        {/* Back Button */}
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>

        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 8H17V4H3C1.9 4 1 4.9 1 6V18C1 19.1 1.9 20 3 20H17C18.1 20 19 19.1 19 18V10H20C20.55 10 21 9.55 21 9V9C21 8.45 20.55 8 20 8ZM19 10V18H3V6H15V8H19V10Z" fill="currentColor"/>
                <path d="M7 15L12 10L15 13L21 7V9H23V4H18V6H20.59L15 11.59L12 8.59L7 13.59L7 15Z" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <h1>Collection & Delivery Confirmation</h1>
              <p>Both parties must confirm product handover to complete the transaction</p>
            </div>
          </div>
          <div className="time-remaining">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM8 14C4.69 14 2 11.31 2 8C2 4.69 4.69 2 8 2C11.31 2 14 4.69 14 8C14 11.31 11.31 14 8 14ZM8.5 4H7V9L11.25 11.15L12 9.92L8.5 8.25V4Z" fill="currentColor"/>
            </svg>
            <span>Time Remaining {timeRemaining?.expired ? "0 hours" : `${timeRemaining?.hours || 0} hours`}</span>
          </div>
        </div>

        {/* Product & Delivery Information */}
        <div className="info-card">
          <div className="info-section product-section">
            <div className="section-header">
              <div className="section-icon orange">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 0L12.5 7.5L20 10L12.5 12.5L10 20L7.5 12.5L0 10L7.5 7.5L10 0Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Product Details</h3>
            </div>
            <div className="section-content">
              <h4>{auction.product_name}</h4>
              <p className="description">{auction.product_description || "No description available"}</p>
              <div className="details-row">
                <span className="label">Final Amount:</span>
                <span className="value">{formatCurrency(payment?.total_amount || auction.winning_bid_amount || 0)}</span>
              </div>
              <div className="details-row">
                <span className="label">Quantity:</span>
                <span className="value">{auction.quantity || 1} {auction.quantity_unit || "Unit"}</span>
              </div>
            </div>
          </div>
          
          <div className="info-section delivery-section">
            <div className="section-header">
              <div className="section-icon green">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM9 5H11V11H9V5ZM9 13H11V15H9V13Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Delivery Information</h3>
            </div>
            <div className="section-content">
              <div className="details-row">
                <span className="label">Collection Date & Time:</span>
                <span className="value">
                  {formatDate(deliveryConfirmation.scheduled_collection_date)} at {formatTime(deliveryConfirmation.scheduled_collection_time)}
                </span>
              </div>
              <div className="details-row">
                <span className="label">Location:</span>
                <span className="value">{deliveryConfirmation.collection_location || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="progress-indicator">
          <div className={`progress-step ${sellerConfirmed ? "completed" : "pending"}`}>
            <div className="step-icon">
              {sellerConfirmed ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                </svg>
              )}
            </div>
            <span className="step-label">{sellerConfirmed ? "Confirmed ✓" : "Pending"}</span>
          </div>
          <div className="progress-arrow">→</div>
          <div className={`progress-step ${buyerConfirmed ? "completed" : "pending"}`}>
            <div className="step-icon">
              {buyerConfirmed ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                </svg>
              )}
            </div>
            <span className="step-label">{buyerConfirmed ? "Confirmed ✓" : "Pending"}</span>
          </div>
          <div className="progress-arrow">→</div>
          <div className={`progress-step ${transactionComplete ? "completed" : "waiting"}`}>
            <div className="step-icon">
              {transactionComplete ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                </svg>
              )}
            </div>
            <span className="step-label">{transactionComplete ? "Completed ✓" : "Waiting"}</span>
          </div>
        </div>

        {/* Confirmation Cards */}
        <div className="confirmation-cards">
          {/* Seller Confirmation Card */}
          <div className={`confirmation-card seller-card ${sellerConfirmed ? "confirmed" : ""}`}>
            {sellerConfirmed && <div className="confirmed-badge">Confirmed</div>}
            <div className="card-header">
              <div className="card-icon orange">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                </svg>
              </div>
              <div>
                <h3>Seller Confirmation</h3>
                <p>Confirm product handover</p>
              </div>
            </div>
            
            {canActAsSeller ? (
              <div className="card-content">
                <div className="company-details">
                  <div className="detail-item">
                    <span className="label">Company Name:</span>
                    <span className="value">{sellerProfile?.company_name || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Contact Person:</span>
                    <span className="value">
                      {(() => {
                        // Try contact_details first, then user_name from user_login, then company_name
                        let contactName = "N/A";
                        if (sellerProfile?.contact_details) {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          contactName = contactDetails?.contact_name || contactName;
                        }
                        if (contactName === "N/A" && sellerProfile?.user_name) {
                          contactName = sellerProfile.user_name;
                        }
                        if (contactName === "N/A" && sellerProfile?.company_name) {
                          contactName = sellerProfile.company_name;
                        }
                        return contactName;
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Phone Number:</span>
                    <span className="value">
                      {(() => {
                        // Try user_phone from user_login, then contact_details, then phone
                        if (sellerProfile?.user_phone) return sellerProfile.user_phone;
                        if (sellerProfile?.contact_details) {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          if (contactDetails?.contact_number) return contactDetails.contact_number;
                        }
                        if (sellerProfile?.phone) return sellerProfile.phone;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Email Address:</span>
                    <span className="value">
                      {(() => {
                        // Try user_email from user_login, then contact_details, then email
                        if (sellerProfile?.user_email) return sellerProfile.user_email;
                        if (sellerProfile?.contact_details) {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          if (contactDetails?.contact_email) return contactDetails.contact_email;
                        }
                        if (sellerProfile?.email) return sellerProfile.email;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                </div>
                
                {sellerConfirmed ? (
                  <div className="status-message">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="#28a745"/>
                    </svg>
                    <span>You have confirmed product handover</span>
                  </div>
                ) : null}
                
                <div className="notes-field">
                  <label>Notes:</label>
                  <textarea
                    value={sellerNotes}
                    onChange={(e) => setSellerNotes(e.target.value)}
                    placeholder="Add any notes about the handover (optional)..."
                    rows={3}
                    disabled={sellerConfirmed}
                  />
                </div>
                
                {!sellerConfirmed && (
                  <button className="btn-confirm orange" onClick={handleSellerConfirmation}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="currentColor"/>
                    </svg>
                    Confirm Product Handover
                  </button>
                )}
              </div>
            ) : (
              <div className="card-content">
                <div className="company-details">
                  <div className="detail-item">
                    <span className="label">Company Name:</span>
                    <span className="value">{sellerProfile?.company_name || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Contact Person:</span>
                    <span className="value">
                      {(() => {
                        // Try contact_details first, then user_name from user_login, then company_name
                        let contactName = "N/A";
                        if (sellerProfile?.contact_details) {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          contactName = contactDetails?.contact_name || contactName;
                        }
                        if (contactName === "N/A" && sellerProfile?.user_name) {
                          contactName = sellerProfile.user_name;
                        }
                        if (contactName === "N/A" && sellerProfile?.company_name) {
                          contactName = sellerProfile.company_name;
                        }
                        return contactName;
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Phone Number:</span>
                    <span className="value">
                      {(() => {
                        // Try user_phone from user_login, then contact_details, then phone
                        if (sellerProfile?.user_phone) return sellerProfile.user_phone;
                        if (sellerProfile?.contact_details) {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          if (contactDetails?.contact_number) return contactDetails.contact_number;
                        }
                        if (sellerProfile?.phone) return sellerProfile.phone;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Email Address:</span>
                    <span className="value">
                      {(() => {
                        // Try user_email from user_login, then contact_details, then email
                        if (sellerProfile?.user_email) return sellerProfile.user_email;
                        if (sellerProfile?.contact_details) {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          if (contactDetails?.contact_email) return contactDetails.contact_email;
                        }
                        if (sellerProfile?.email) return sellerProfile.email;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                </div>
                {sellerConfirmed && deliveryConfirmation.seller_notes && (
                  <div className="notes-display">
                    <span className="label">Notes:</span>
                    <span className="value">{deliveryConfirmation.seller_notes}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Buyer Confirmation Card */}
          <div className={`confirmation-card buyer-card ${buyerConfirmed ? "confirmed" : ""}`}>
            {buyerConfirmed && <div className="confirmed-badge">Confirmed</div>}
            <div className="card-header">
              <div className="card-icon blue">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                </svg>
              </div>
              <div>
                <h3>Buyer Confirmation</h3>
                <p>Confirm product receipt</p>
              </div>
            </div>
            
            {canActAsBuyer ? (
              <div className="card-content">
                <div className="company-details">
                  <div className="detail-item">
                    <span className="label">Company Name:</span>
                    <span className="value">{buyerProfile?.company_name || buyerProfile?.companyName || (isBuyer && user?.company_name) || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Contact Person:</span>
                    <span className="value">
                      {(() => {
                        let contactName = "N/A";
                        if (buyerProfile?.contact_details) {
                          try {
                            const contactDetails = typeof buyerProfile.contact_details === 'string' 
                              ? JSON.parse(buyerProfile.contact_details) 
                              : buyerProfile.contact_details;
                            contactName = contactDetails?.contact_name || contactName;
                          } catch (_) {}
                        }
                        if (contactName === "N/A" && (buyerProfile?.user_name || buyerProfile?.userName)) contactName = buyerProfile.user_name || buyerProfile.userName;
                        if (contactName === "N/A" && (buyerProfile?.company_name || buyerProfile?.companyName)) contactName = buyerProfile.company_name || buyerProfile.companyName;
                        if (contactName === "N/A" && isBuyer && (user?.name || user?.user_name)) contactName = user.name || user.user_name;
                        return contactName;
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Phone Number:</span>
                    <span className="value">
                      {(() => {
                        if (buyerProfile?.user_phone) return buyerProfile.user_phone;
                        if (buyerProfile?.contact_details) {
                          try {
                            const contactDetails = typeof buyerProfile.contact_details === 'string' ? JSON.parse(buyerProfile.contact_details) : buyerProfile.contact_details;
                            if (contactDetails?.contact_number) return contactDetails.contact_number;
                          } catch (_) {}
                        }
                        if (buyerProfile?.phone) return buyerProfile.phone;
                        if (isBuyer && user?.phone) return user.phone;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Email Address:</span>
                    <span className="value">
                      {(() => {
                        if (buyerProfile?.user_email) return buyerProfile.user_email;
                        if (buyerProfile?.contact_details) {
                          try {
                            const contactDetails = typeof buyerProfile.contact_details === 'string' ? JSON.parse(buyerProfile.contact_details) : buyerProfile.contact_details;
                            if (contactDetails?.contact_email) return contactDetails.contact_email;
                          } catch (_) {}
                        }
                        if (buyerProfile?.email) return buyerProfile.email;
                        if (isBuyer && user?.email) return user.email;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                </div>
                
                {buyerConfirmed ? (
                  <div className="status-message">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="#28a745"/>
                    </svg>
                    <span>You have confirmed product receipt</span>
                  </div>
                ) : null}
                
                <div className="notes-field">
                  <label>Notes:</label>
                  <textarea
                    value={buyerNotes}
                    onChange={(e) => setBuyerNotes(e.target.value)}
                    placeholder="Add any notes about the received product (optional)..."
                    rows={3}
                    disabled={buyerConfirmed}
                  />
                </div>
                
                {!buyerConfirmed && (
                  <button className="btn-confirm blue" onClick={handleBuyerConfirmation}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="currentColor"/>
                    </svg>
                    Confirm Product Receipt
                  </button>
                )}
              </div>
            ) : (
              <div className="card-content">
                <div className="company-details">
                  <div className="detail-item">
                    <span className="label">Company Name:</span>
                    <span className="value">{buyerProfile?.company_name || buyerProfile?.companyName || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Contact Person:</span>
                    <span className="value">
                      {(() => {
                        let contactName = "N/A";
                        if (buyerProfile?.contact_details) {
                          try {
                            const contactDetails = typeof buyerProfile.contact_details === 'string' ? JSON.parse(buyerProfile.contact_details) : buyerProfile.contact_details;
                            contactName = contactDetails?.contact_name || contactName;
                          } catch (_) {}
                        }
                        if (contactName === "N/A" && (buyerProfile?.user_name || buyerProfile?.userName)) contactName = buyerProfile.user_name || buyerProfile.userName;
                        if (contactName === "N/A" && (buyerProfile?.company_name || buyerProfile?.companyName)) contactName = buyerProfile.company_name || buyerProfile.companyName;
                        return contactName;
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Phone Number:</span>
                    <span className="value">
                      {(() => {
                        if (buyerProfile?.user_phone) return buyerProfile.user_phone;
                        if (buyerProfile?.contact_details) {
                          try {
                            const contactDetails = typeof buyerProfile.contact_details === 'string' ? JSON.parse(buyerProfile.contact_details) : buyerProfile.contact_details;
                            if (contactDetails?.contact_number) return contactDetails.contact_number;
                          } catch (_) {}
                        }
                        if (buyerProfile?.phone) return buyerProfile.phone;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Email Address:</span>
                    <span className="value">
                      {(() => {
                        if (buyerProfile?.user_email) return buyerProfile.user_email;
                        if (buyerProfile?.contact_details) {
                          try {
                            const contactDetails = typeof buyerProfile.contact_details === 'string' ? JSON.parse(buyerProfile.contact_details) : buyerProfile.contact_details;
                            if (contactDetails?.contact_email) return contactDetails.contact_email;
                          } catch (_) {}
                        }
                        if (buyerProfile?.email) return buyerProfile.email;
                        return "N/A";
                      })()}
                    </span>
                  </div>
                </div>
                {buyerConfirmed && deliveryConfirmation.buyer_notes && (
                  <div className="notes-display">
                    <span className="label">Notes:</span>
                    <span className="value">{deliveryConfirmation.buyer_notes}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Important Notice */}
        <div className="notice-card">
          <div className="notice-header">
            <div className="notice-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
              </svg>
            </div>
            <h3>Important Notice</h3>
          </div>
          <div className="notice-content">
            <p>Trocanet operates as a neutral digital facilitator. Goods remain under seller ownership and responsibility until buyer confirms receipt.</p>
            <p>Both parties must confirm within 72 hours of scheduled delivery time. Failure to confirm may result in dispute resolution.</p>
          </div>
        </div>

        {/* Transaction Complete */}
        {transactionComplete && (
          <div className="transaction-complete-card">
            <div className="complete-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="32" fill="#28a745"/>
                <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Transaction Complete! 🎉</h2>
            <p>Both parties have confirmed the handover. The auction transaction is now complete.</p>
            <div className="transaction-details">
              <span>Transaction ID: #{auction.auction_id}</span>
              <span>Completion Date: {formatDate(new Date())}</span>
            </div>
            <button
              className="btn-view-completion"
              onClick={() => navigate(`/auction/${auctionId}/transaction-completion`)}
            >
              View Transaction Completion
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
